import { Link, useParams, useSearch } from "@tanstack/react-router";
import { Lock } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AgencyContactDialog } from "@/components/agency/AgencyContactDialog";
import { AgencySummaryCard } from "@/components/agency/AgencySummaryCard";
import { ScorecardGroupDialog } from "@/components/agency/ScorecardGroupDialog";
import { EmployerContactDialog } from "@/components/policy/EmployerContactDialog";
import { PolicySummaryCard } from "@/components/policy/PolicySummaryCard";
import { ServiceTeamDialog } from "@/components/policy/ServiceTeamDialog";
import { ErrorState } from "@/components/shared/error-state";
import { FavoriteToggle } from "@/components/shared/favorite-toggle";
import { type BreadcrumbTrailItem, PageBreadcrumbs } from "@/components/shared/page-breadcrumbs";
import { PanelState } from "@/components/shared/panel-state";
import { Button } from "@/components/ui/button";
import { SectionBar } from "@/components/ui/section-bar";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/ui/status-badge";
import { TabBar } from "@/components/ui/tab-bar";
import { useClaim, useClaimAgency, useClaimFinancials, useClaimPolicy } from "@/hooks/use-claim";
import { usePolicyClaimCounts } from "@/hooks/use-policy";
import { useSyncUserDisplay } from "@/hooks/use-user-display";
import { parseFrom } from "@/lib/breadcrumb-from";
import { formatCurrency, formatDate, formatPercentage } from "@/lib/format";
import { claimStatusKey } from "@/lib/status-colors";
import { claimsDashboardSearchSchema } from "@/schemas/search";

type ClaimTab = "Claim Detail" | "Injured Worker" | "Medical Info" | "Claim Financials";
const CLAIM_TABS: ClaimTab[] = [
  "Claim Detail",
  "Injured Worker",
  "Medical Info",
  "Claim Financials",
];

function yesNo(value: string | null | undefined): string {
  if (!value) return "—";
  const upper = value.toUpperCase();
  if (upper === "Y" || upper === "YES" || upper === "TRUE") return "Yes";
  if (upper === "N" || upper === "NO" || upper === "FALSE") return "No";
  return value;
}

function Field({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) {
  return (
    <div className="min-w-0">
      <div className="text-muted-foreground text-[11px] font-medium">
        {hint ? (
          <span
            title={hint}
            className="cursor-help underline decoration-dotted decoration-muted-foreground/40 underline-offset-2"
          >
            {label}
          </span>
        ) : (
          label
        )}
      </div>
      <div className="mt-0.5 break-words font-medium text-sm">{value ?? "—"}</div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="mb-3 font-medium text-primary text-sm">{title}</h3>
      {children}
    </section>
  );
}

export function ClaimsDashboardPage() {
  const { claimNumber } = useParams({ strict: false }) as { claimNumber: string };
  const search = claimsDashboardSearchSchema.safeParse(useSearch({ strict: false }));
  const fromHint = search.success ? parseFrom(search.data.from) : null;
  const [scorecardOpen, setScorecardOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [serviceTeamOpen, setServiceTeamOpen] = useState(false);
  const [employerContactOpen, setEmployerContactOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<ClaimTab>("Claim Detail");

  // The router can reuse the route component instance when claimNumber
  // changes, so useState alone wouldn't reset the active tab or close any
  // dialogs left open on the previous claim. Without resetting the dialog
  // flags, a Service Team or Agency Contact modal opened on the prior
  // claim would reappear on the new one with stale data.
  useEffect(() => {
    if (claimNumber) {
      setActiveTab("Claim Detail");
      setScorecardOpen(false);
      setContactOpen(false);
      setServiceTeamOpen(false);
      setEmployerContactOpen(false);
    }
  }, [claimNumber]);

  const { data: claim, isLoading, isError, refetch } = useClaim(claimNumber);
  const isRestricted = claim?.isRestricted === true;
  // When the claim is restricted from the current user, skip the secondary
  // fetches — the API withholds the payload, so policy/agency/financials
  // would either error or leak adjacent info. The banner branch renders
  // before any of these are read.
  const policyQuery = useClaimPolicy(claimNumber, !isRestricted);
  const agencyQuery = useClaimAgency(claimNumber, !isRestricted);
  const financialsQuery = useClaimFinancials(claimNumber, !isRestricted);
  const policyData = policyQuery.data;
  const agencyData = agencyQuery.data;
  const financials = financialsQuery.data;
  const policyNumber = policyData?.claimPolicy?.policyNumber;
  const { data: claimCounts } = usePolicyClaimCounts(policyNumber ?? "");

  // Only sync once the claim actually loads — visiting a bogus claim
  // shouldn't pollute UserDisplay with a non-existent claim.
  useSyncUserDisplay({
    claimNumber: claim ? claimNumber : null,
    policyNumber: claim ? (policyNumber ?? null) : null,
  });

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-5 w-48" />
        <div className="grid gap-4 md:grid-cols-12">
          {/* Left column — Policy summary then Agency summary */}
          <div className="space-y-4 md:col-span-5">
            <Skeleton className="h-9 w-full" />
            <div className="space-y-3 rounded-md border bg-card p-4">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-4 w-3/5" />
              <div className="grid grid-cols-2 gap-3 pt-2">
                <Skeleton className="h-12" />
                <Skeleton className="h-12" />
              </div>
            </div>
            <Skeleton className="h-9 w-full" />
            <div className="space-y-3 rounded-md border bg-card p-4">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          </div>
          {/* Right column — Claim header + tabs + body grid */}
          <div className="space-y-3 md:col-span-7">
            <Skeleton className="h-9 w-full" />
            <div className="space-y-4 rounded-md border bg-card p-4">
              <div className="flex gap-2 border-b pb-2">
                <Skeleton className="h-7 w-24" />
                <Skeleton className="h-7 w-28" />
                <Skeleton className="h-7 w-24" />
                <Skeleton className="h-7 w-32" />
              </div>
              <div className="grid grid-cols-3 gap-x-4 gap-y-4">
                {Array.from({ length: 8 }, (_, idx) => `claim-field-skel-${idx}`).map((k) => (
                  <div key={k} className="space-y-1.5">
                    <Skeleton className="h-3 w-20" />
                    <Skeleton className="h-4 w-3/4" />
                  </div>
                ))}
              </div>
              <Skeleton className="h-16 w-full" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return <ErrorState message="Failed to load claim." onRetry={() => refetch()} />;
  }

  if (!claim) {
    return (
      <div className="py-12 text-center">
        <h2 className="font-semibold text-xl">Claim Not Found</h2>
        <p className="mt-2 text-muted-foreground">No claim found with number {claimNumber}.</p>
        <Button variant="outline" className="mt-4" asChild>
          <Link to="/home">Back to Search</Link>
        </Button>
      </div>
    );
  }

  if (isRestricted) {
    return (
      <div className="space-y-3">
        <PageBreadcrumbs current={`Claim ${claimNumber}`} />
        <div className="mx-auto max-w-2xl py-12">
          <SectionBar title={`Claim No. ${claimNumber}`} />
          <div className="rounded-b-md border border-t-0 bg-card p-6">
            <div className="flex items-start gap-3">
              <span
                className="flex size-9 shrink-0 items-center justify-center rounded-full"
                style={{
                  backgroundColor: "var(--status-restricted-bg)",
                  color: "var(--status-restricted-fg)",
                }}
              >
                <Lock className="size-4" aria-hidden="true" />
              </span>
              <div>
                <h2 className="font-semibold text-lg">Restricted Claim</h2>
                <p className="mt-1 text-muted-foreground text-sm">
                  {claim.restrictedMessage ?? "You don't have access to this claim."}
                </p>
              </div>
            </div>
            <div className="mt-6">
              <Button variant="outline" asChild>
                <Link to="/home">Go Back</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const policy = policyData?.policy;
  const insured = policyData?.insured;
  const agent = agencyData?.agent;
  const agency = agencyData?.agency;
  const claimantName = [claim.firstName, claim.lastName].filter(Boolean).join(" ") || null;
  const claimantAddress =
    [
      claim.claimantAddress,
      [claim.claimantCity, claim.claimantState, claim.claimantZipCode].filter(Boolean).join(", "),
    ]
      .filter(Boolean)
      .join(" · ") || null;

  return (
    <div className="space-y-3">
      <PageBreadcrumbs
        trail={
          fromHint?.kind === "policy"
            ? ([
                {
                  key: "policy",
                  link: (
                    <Link
                      to="/policy/$policyNumber"
                      params={{ policyNumber: fromHint.policyNumber }}
                    >
                      Policy {fromHint.policyNumber}
                    </Link>
                  ),
                },
                {
                  key: "claims",
                  link: (
                    <Link to="/claims/mrp" search={{ policyNumber: fromHint.policyNumber }}>
                      Claims
                    </Link>
                  ),
                },
              ] satisfies BreadcrumbTrailItem[])
            : fromHint?.kind === "agency"
              ? ([
                  {
                    key: "agency",
                    link: (
                      <Link
                        to="/agency/$agentNumber"
                        params={{ agentNumber: fromHint.agentNumber }}
                      >
                        Agency {fromHint.agentNumber}
                      </Link>
                    ),
                  },
                  {
                    key: "claims",
                    link: (
                      <Link to="/claims/mrp" search={{ agentNumber: fromHint.agentNumber }}>
                        Claims
                      </Link>
                    ),
                  },
                ] satisfies BreadcrumbTrailItem[])
              : undefined
        }
        current={`Claim ${claimNumber}`}
      />

      <div className="grid gap-4 md:grid-cols-12">
        {/* ─── LEFT: Policy + Agency ─── */}
        <div
          key={`left-${claimNumber}`}
          className="space-y-4 animate-in fade-in slide-in-from-bottom-4 [animation-duration:800ms] [animation-fill-mode:backwards] md:col-span-5"
        >
          {/* Policy */}
          {policyNumber ? (
            <SectionBar
              title={`Policy No. ${policyNumber}`}
              linkTo="/policy/$policyNumber"
              linkParams={{ policyNumber }}
              copyValue={policyNumber}
              copyLabel={`policy ${policyNumber}`}
              rightSlot={
                <FavoriteToggle
                  kind="policy"
                  number={policyNumber}
                  name={insured?.insuredName ?? policy?.insuredName ?? null}
                />
              }
            />
          ) : (
            <SectionBar title="Policy" />
          )}
          <div className="-mt-4 rounded-md rounded-t-none border border-t-0 bg-card p-4">
            <PolicySummaryCard
              policyNumber={policyNumber}
              insuredName={insured?.insuredName ?? policy?.insuredName ?? null}
              insuredAddress={insured?.insuredAddress ?? null}
              inceptionDate={policy?.inceptionDate ?? null}
              expirationDate={policy?.expirationDate ?? null}
              claimCounts={claimCounts}
              actions={
                policyNumber ? (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 rounded-md px-2.5 text-xs"
                      onClick={() =>
                        toast.info(
                          "Claim Service Summary opens in an external application (coming soon)",
                        )
                      }
                    >
                      Claim Service Summary
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 rounded-md px-2.5 text-xs"
                      onClick={() => setServiceTeamOpen(true)}
                    >
                      Service Team
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 rounded-md px-2.5 text-xs"
                      onClick={() => setEmployerContactOpen(true)}
                    >
                      Employer Contact
                    </Button>
                  </>
                ) : undefined
              }
            />
          </div>

          {/* Agency */}
          <SectionBar
            title={agent ? `Agency No. ${agent.agentNumber}` : "Agency"}
            linkTo={agent ? "/agency/$agentNumber" : undefined}
            linkParams={agent ? { agentNumber: agent.agentNumber } : undefined}
            copyValue={agent?.agentNumber}
            copyLabel={agent ? `agency ${agent.agentNumber}` : undefined}
            rightSlot={
              agent ? (
                <FavoriteToggle
                  kind="agency"
                  number={agent.agentNumber}
                  name={agency?.agencyName ?? null}
                />
              ) : undefined
            }
          />
          <div className="-mt-4 rounded-md rounded-t-none border border-t-0 bg-card p-4">
            {agencyQuery.isLoading ? (
              <PanelState loading loadingText="Loading agency…" />
            ) : agencyQuery.isError ? (
              <PanelState
                error={agencyQuery.error}
                errorMessage="Failed to load agency."
                onRetry={() => agencyQuery.refetch()}
              />
            ) : agent ? (
              <AgencySummaryCard
                agent={agent}
                agency={agency}
                actions={
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 rounded-md px-2.5 text-xs"
                      onClick={() => setScorecardOpen(true)}
                    >
                      Scorecard Group
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 rounded-md px-2.5 text-xs"
                      onClick={() => setContactOpen(true)}
                    >
                      Agency Contact
                    </Button>
                  </>
                }
              />
            ) : (
              <PanelState empty emptyMessage="No agency on file for this claim." />
            )}
          </div>
        </div>

        {/* ─── RIGHT: Claim detail ─── */}
        <div
          key={`right-${claimNumber}`}
          className="animate-in fade-in slide-in-from-bottom-4 [animation-duration:800ms] [animation-fill-mode:backwards] [animation-delay:200ms] md:col-span-7"
        >
          <SectionBar
            title={`Claim No. ${claimNumber}`}
            subtitle={claim.currentStatus ?? undefined}
            copyValue={claimNumber}
            copyLabel={`claim ${claimNumber}`}
            rightSlot={<FavoriteToggle kind="claim" number={claimNumber} name={claimantName} />}
          />
          <div className="space-y-6 rounded-md rounded-t-none border border-t-0 bg-card p-4">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b">
              <TabBar
                ariaLabel="Claim information"
                activeKey={activeTab}
                onChange={setActiveTab}
                tabs={CLAIM_TABS.map((tab) => ({ key: tab, label: tab }))}
                className="border-b-0"
              />
              <div className="pb-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 rounded-md px-2.5 text-xs"
                  onClick={() =>
                    toast.info("Claim Detail PDF opens in an external application (coming soon)")
                  }
                >
                  Claim Detail PDF
                </Button>
              </div>
            </div>

            {activeTab === "Claim Detail" && (
              <>
                <section>
                  <div className="grid grid-cols-3 gap-x-4 gap-y-3">
                    <Field label="Claim No." value={claim.claimNumber} />
                    <Field label="Policy No." value={policyNumber} />
                    <Field label="Agency No." value={agent?.agentNumber} />
                    <Field label="Loss Date" value={formatDate(claim.lossDate)} />
                    <Field label="First Report of Injury" value={formatDate(claim.reportDate)} />
                    <Field
                      label="Claim Status"
                      value={
                        claim.currentStatus ? (
                          <StatusBadge
                            statusKey={claimStatusKey(claim.currentStatus)}
                            label={claim.currentStatus}
                          />
                        ) : null
                      }
                    />
                    <Field label="MEMIC Attorney" value={claim.memicAttorneyName ?? "No"} />
                    <div className="col-span-2">
                      <Field label="Claim Adjuster" value={claim.adjusterName} />
                    </div>
                  </div>
                </section>

                <Section title="Description of Incident">
                  <div className="rounded-md border bg-muted/30 p-3 text-sm">
                    {claim.injuryDescription ?? (
                      <span className="text-muted-foreground">No description on file</span>
                    )}
                  </div>
                </Section>

                <Section title="Current Status">
                  <div className="rounded-md border bg-muted/30 p-3 text-sm">
                    {claim.claimStatusDescription ?? (
                      <span className="text-muted-foreground">No status notes on file</span>
                    )}
                  </div>
                </Section>

                <Section title="Action Plan">
                  <div className="rounded-md border bg-muted/30 p-3 text-sm">
                    {claim.actionPlan ?? (
                      <span className="text-muted-foreground">No action plan on file</span>
                    )}
                  </div>
                </Section>
              </>
            )}

            {activeTab === "Injured Worker" && (
              <div className="grid grid-cols-3 gap-x-4 gap-y-3">
                <Field label="Name" value={claimantName} />
                <div className="col-span-1">
                  <Field label="Occupation" value={claim.occupationDescription} />
                </div>
                <Field
                  label="Average Weekly Wage"
                  value={formatCurrency(claim.averageWeeklyWage)}
                />
                <Field
                  label="Total Compensation Rate"
                  value={formatCurrency(claim.compensationRate)}
                />
                <Field label="Education Level" value={claim.educationLevel} />
                <div />
                <Field label="Injured Worker Attorney" value={yesNo(claim.injuredWorkerAttorney)} />
                <Field
                  label="Current Rate Being Paid"
                  value={formatCurrency(claim.currentTotalCompensationRatePaid)}
                />
                <Field label="Attorney Name" value={claim.attorneyName} />
                <div className="col-span-3">
                  <Field label="Address" value={claimantAddress} />
                </div>
              </div>
            )}

            {activeTab === "Medical Info" && (
              <div className="grid grid-cols-3 gap-x-4 gap-y-3">
                <Field label="Physician" value={claim.physicianName} />
                <Field label="Capacity To Work?" value={claim.employeeWorkCapacity} />
                <Field
                  label="RTW Light Duty"
                  value={formatDate(claim.rtwLightDuty)}
                  hint="Return To Work — Light Duty: date the injured worker can return to work in a restricted capacity."
                />
                <Field label="Current Diagnosis" value={claim.currentDiagnosis} />
                <Field
                  label="MMI Reached?"
                  value={yesNo(claim.mmiReached)}
                  hint="Maximum Medical Improvement — whether the injured worker has reached the point where no further recovery is expected."
                />
                <Field
                  label="RTW Full Duty"
                  value={formatDate(claim.rtwFullDuty)}
                  hint="Return To Work — Full Duty: date the injured worker can return to their pre-injury role without restrictions."
                />
                <Field
                  label="Date Of Incapacity"
                  value={formatDate(claim.dateOfIncapacity)}
                  hint="The date the injured worker became unable to work due to the injury (legacy 'Disability Date')."
                />
                <Field
                  label="PI Expected?"
                  value={yesNo(claim.permanentImpairmentExpected)}
                  hint="Permanent Impairment Expected — whether the injury is expected to leave a permanent functional limitation."
                />
                <Field
                  label="PI Rating"
                  value={formatPercentage(claim.piRating)}
                  hint="Permanent Impairment Rating — the assessed degree of permanent functional loss, expressed as a percentage."
                />
              </div>
            )}

            {activeTab === "Claim Financials" &&
              (financialsQuery.isLoading ? (
                <PanelState loading loadingText="Loading financials…" />
              ) : financialsQuery.isError ? (
                <PanelState
                  error={financialsQuery.error}
                  errorMessage="Failed to load financials."
                  onRetry={() => financialsQuery.refetch()}
                />
              ) : financials ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-muted-foreground text-xs">
                        <th className="pb-2 text-left font-medium" />
                        <th className="pb-2 text-right font-medium">Reserve</th>
                        <th className="pb-2 text-right font-medium">Paid</th>
                        <th className="pb-2 text-right font-medium">Outstanding</th>
                        <th className="pb-2 text-right font-medium">Net Incurred</th>
                      </tr>
                    </thead>
                    <tbody>
                      {financials.byType.map((row) => (
                        <tr key={row.type} className="border-b last:border-0">
                          <td className="py-1.5 text-muted-foreground">{row.type}</td>
                          <td className="py-1.5 text-right font-medium tabular-nums">
                            {formatCurrency(row.reserved)}
                          </td>
                          <td className="py-1.5 text-right font-medium tabular-nums">
                            {formatCurrency(row.paid)}
                          </td>
                          <td className="py-1.5 text-right font-medium tabular-nums">
                            {formatCurrency(row.outstanding)}
                          </td>
                          <td className="py-1.5 text-right font-medium tabular-nums">
                            {formatCurrency(row.netIncurred)}
                          </td>
                        </tr>
                      ))}
                      <tr className="border-t-2 font-semibold">
                        <td className="pt-2">Total</td>
                        <td className="pt-2 text-right tabular-nums">
                          {formatCurrency(financials.totals.reserved)}
                        </td>
                        <td className="pt-2 text-right tabular-nums">
                          {formatCurrency(financials.totals.paid)}
                        </td>
                        <td className="pt-2 text-right tabular-nums">
                          {formatCurrency(financials.totals.outstanding)}
                        </td>
                        <td className="pt-2 text-right tabular-nums">
                          {formatCurrency(financials.totals.netIncurred)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              ) : (
                <PanelState empty emptyMessage="No financial data on file." />
              ))}
          </div>
        </div>

        {agent?.agentNumber && (
          <>
            <ScorecardGroupDialog
              agentNumber={agent.agentNumber}
              open={scorecardOpen}
              onOpenChange={setScorecardOpen}
            />
            <AgencyContactDialog
              agentNumber={agent.agentNumber}
              open={contactOpen}
              onOpenChange={setContactOpen}
            />
          </>
        )}
        {policyNumber && (
          <>
            <ServiceTeamDialog
              policyNumber={policyNumber}
              open={serviceTeamOpen}
              onOpenChange={setServiceTeamOpen}
            />
            <EmployerContactDialog
              policyNumber={policyNumber}
              open={employerContactOpen}
              onOpenChange={setEmployerContactOpen}
            />
          </>
        )}
      </div>
    </div>
  );
}
